import { useEffect, useRef, useCallback } from 'react';
import { createRoot } from 'react-dom/client';
import { ShieldAlert, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

/* ── Standalone popup (rendered via createRoot, like adminToast.jsx) ── */

const ProtectionPopup = ({ message, title, onDismiss }) => {
    useEffect(() => {
        const timer = setTimeout(onDismiss, 3500);
        return () => clearTimeout(timer);
    }, [onDismiss]);

    return (
        <div className="fixed inset-0 z-[300] flex items-center justify-center pointer-events-none">
            <div
                className="pointer-events-auto bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-white/10 px-6 py-4 max-w-sm w-full mx-4 flex items-center gap-4"
                style={{ animation: 'protection-popup-in 200ms ease-out' }}
            >
                <div className="shrink-0 p-2.5 bg-amber-100 dark:bg-amber-500/20 rounded-xl">
                    <ShieldAlert size={22} className="text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{message}</p>
                </div>
                <button
                    onClick={onDismiss}
                    className="shrink-0 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded transition"
                >
                    <X size={16} />
                </button>
            </div>
        </div>
    );
};

function showProtectionPopup(title, message) {
    const container = document.createElement('div');
    document.body.appendChild(container);
    const root = createRoot(container);

    const dismiss = () => {
        root.unmount();
        container.remove();
    };

    root.render(<ProtectionPopup title={title} message={message} onDismiss={dismiss} />);
}

/* ── Inject popup keyframe once ── */

let styleInjected = false;
function injectStyle() {
    if (styleInjected) return;
    styleInjected = true;
    const style = document.createElement('style');
    style.textContent = `
        @keyframes protection-popup-in {
            0% { transform: scale(0.92); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
        }
    `;
    document.head.appendChild(style);
}

/* ── Main component (renders nothing, purely behavioral) ── */

const ContentProtection = () => {
    const { isAdmin } = useAuth();
    const { language } = useLanguage();
    const lastPopupTime = useRef(0);
    const isProtected = !isAdmin; // protège tout le monde sauf les admins

    // Inject animation keyframe
    useEffect(() => { injectStyle(); }, []);

    // Manage body class for CSS user-select override
    useEffect(() => {
        if (isAdmin) {
            document.body.classList.add('preview-mode');
        } else {
            document.body.classList.remove('preview-mode');
        }
        return () => document.body.classList.remove('preview-mode');
    }, [isAdmin]);

    const triggerPopup = useCallback(() => {
        const now = Date.now();
        if (now - lastPopupTime.current < 3000) return;
        lastPopupTime.current = now;
        const title = language === 'fr' ? 'Contenu protégé' : 'Protected content';
        const message = language === 'fr'
            ? 'La copie du contenu est interdite.'
            : 'Content copying is not allowed.';
        showProtectionPopup(title, message);
    }, [language]);

    const isFormElement = useCallback((el) => {
        if (!el) return false;
        const tag = el.tagName?.toLowerCase();
        if (['input', 'textarea', 'select'].includes(tag)) return true;
        if (el.isContentEditable) return true;
        if (el.closest?.('input, textarea, select, [contenteditable="true"]')) return true;
        return false;
    }, []);

    useEffect(() => {
        if (!isProtected) return;

        const handleContextMenu = (e) => {
            if (isFormElement(e.target)) return;
            e.preventDefault();
            triggerPopup();
        };

        const handleCopy = (e) => {
            if (isFormElement(e.target)) return;
            e.preventDefault();
            triggerPopup();
        };

        const handleCut = (e) => {
            if (isFormElement(e.target)) return;
            e.preventDefault();
            triggerPopup();
        };

        const handleKeyDown = (e) => {
            if (isFormElement(e.target)) return;
            if ((e.ctrlKey || e.metaKey) && ['c', 'x', 'a'].includes(e.key.toLowerCase())) {
                e.preventDefault();
                triggerPopup();
            }
        };

        document.addEventListener('contextmenu', handleContextMenu, true);
        document.addEventListener('copy', handleCopy, true);
        document.addEventListener('cut', handleCut, true);
        document.addEventListener('keydown', handleKeyDown, true);

        return () => {
            document.removeEventListener('contextmenu', handleContextMenu, true);
            document.removeEventListener('copy', handleCopy, true);
            document.removeEventListener('cut', handleCut, true);
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [isProtected, triggerPopup, isFormElement]);

    return null;
};

export default ContentProtection;
